import React, {useEffect, useState} from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';


// Components
import LinkIcon from "../../../application/LinkIcon";
import SmartTableToolbar from "./SmartTableToolbar";
import SmartTableHead from "./SmartTableHead";
import useDataSource from "../../../../hooks/useDataSource";

import {
    getRAGStyleProperty,
    getRAGStyleValue
} from "../../../../utilities/ColorUtilities";

import {
    getFieldAlias,
    getFieldFormatters
} from "../../../../utilities/DataSourceUtilities";

import {
	formatDate,
	formatAvatar,
	formatNumber,
	formatStyle,
	formatText,
	formatSuffix,
	FormatLink,
	FormatModalLink,
	formatGoodBad,
	formatDuration,
	formatValue
} from "../../../../utilities/FormatUtilities";

import {
	dataAddFilter,
	dataReloadDependants,
	dataReloadSources, filterUpdateConditionAction
} from "../../../../redux/data/dataActions";

import * as DataUtilities from "../../../../utilities/DataUtilities";
import Construct from "../../Construct";
import {compareValues} from "../../../../utilities/DataUtilities";
import SettingsForm from "../settings/SettingsForm";
import {appSetAlert, appSetModalContent} from "../../../../redux/app/appActions";
import {findComponent} from "../../../../redux/page/pageActions";

import axios from "axios";
import {axiosDelay} from "../../../../config/config";
import ConfirmationButton from "../../../generic/ConfirmationButton";


import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableRow,
    Typography,
    Paper,
    Collapse,
    IconButton,
    LinearProgress,
} from '@mui/material';


const PREFIX = 'SmartTable';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    paper: `${PREFIX}-paper`,
    tableContainer: `${PREFIX}-tableContainer`,
    tableContainerSansPager: `${PREFIX}-tableContainerSansPager`,
    table: `${PREFIX}-table`,
    visuallyHidden: `${PREFIX}-visuallyHidden`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
	[`&.${classes.root}`]: {
        width: '100%',
        height: 'calc(100% - 1px)',
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.paper}`]: {
        height: '100%',
        width: '100%',
        boxShadow: 'none'
    },

    [`& .${classes.tableContainer}`]: {
        height: 'calc(100% - 116px)',
        width: '100%',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },

    [`& .${classes.tableContainerSansPager}`]: {
        height: 'auto',
        width: '100%',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },

    [`& .${classes.table}`]: {
        minWidth: 200,
        width: '100%',
        //height: 'calc(100% - 52px)'
    },

    [`& .${classes.visuallyHidden}`]: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    }
}));

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export default function SmartTable({id, configuration, collapsibleFilterValue, xId, onLoading, onLoaded}) {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // App State
	const config = useSelector(state => state.config);
    const ragStyle = useSelector(state => state.config.data.ragStyle);
    const dataDate = useSelector(state => state.data.dataDate);
    const dataSources  = useSelector(state => state.data.dataSources);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

	// Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
    const [columns, setColumns] = React.useState([]);
    const [rows, setRows] = React.useState([]);
    const [selected, setSelected] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [dense, setDense] = React.useState(compConfig.element?.densePadding ? compConfig.element.densePadding : 'medium');
    const [minimumWidth, setMinimumWidth] = React.useState(compConfig.element?.minWidth ? compConfig.element.minWidth : '600');
    const [variant, setVariant] = React.useState(compConfig.element?.typeVariant ? compConfig.element.typeVariant : 'body2');
    const [rowsPerPage, setRowsPerPage] = React.useState(compConfig.element?.defaultRowsPerPage ? compConfig.element.defaultRowsPerPage : 5);
    const [showPager, setPager] = React.useState(true);
    const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
    // const [bkgColor, setBkgColor] = useState('');

	const titleVariant = compConfig.name?.titleVariant ? compConfig.name.titleVariant : null;
	const subTitleVariant = compConfig.name?.subTitleVariant ? compConfig.name.subTitleVariant : null;
	const pivotEnabled = compConfig.element?.pivot !== undefined ? compConfig.element.pivot.enabled : false;
	const pivotHeading = compConfig.element?.pivot !== undefined ? compConfig.element.pivot.heading : false;
	const rowsPerPageOptions = compConfig.element?.rowsPerPageOptions ? compConfig.element.rowsPerPageOptions : [5, 10, 25];
	const showPagination = compConfig.element?.showPagination !== undefined ? compConfig.element.showPagination : true;
	const sort = compConfig.element?.sort ? compConfig.element.sort : {order: 'asc', orderBy: ''};
	const collapsible = compConfig.element?.collapsible?.enabled !== undefined ? compConfig.element.collapsible.enabled : false;
	const addData = compConfig.element?.addData !== undefined ? compConfig.element.addData : {};
	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource.name : '';
	const fields = compConfig.dataSource?.fields ? compConfig.dataSource.fields : [];
	const alwaysLoadDataSource = compConfig.dataSource?.alwaysLoadDataSource ? compConfig.dataSource.alwaysLoadDataSource : false;
	const idField = compConfig.dataSource?.idField ? compConfig.dataSource.idField : null;
	const condition = compConfig.dataSource?.condition ? compConfig.dataSource.condition : null;

	const dataSource = useDataSource(dataSourceName);

	const [order, setOrder] = React.useState(sort ? sort.order : 'asc');
	const [orderBy, setOrderBy] = React.useState(sort ? sort.orderBy : '');

	useEffect(() => {
		if (alwaysLoadDataSource) {
			const interval = setInterval(() => {
				dispatch(dataReloadSources([dataSource.name]));
			}, 20000);

			return () => clearInterval(interval);
		}
	}, []);

    // Loading
    useEffect(() => {
        if (dataSource.fetching === true) {
            if (isFunction(onLoading)) {
                onLoading();
            }
			setShowProgress(true);
        }
    }, [dataSource.fetching]);

    // Loaded
    useEffect(() => {
        if (dataSource.fetching === false && dataSource.fetched === true) {
            if (isFunction(onLoaded)) {
                onLoaded();
            }
			setShowProgress(false);
        }
    }, [dataSource.fetched]);

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
			}
		}
	}, [componentUpdated]);

    useEffect(() => {

        if (dataSource.error) {
            // TODO: No Data Message
        }
        else if (dataSource.data && dataSource.fetched) {

            let data;
			let filteredData;
            if (condition) {
				const conditionValue = isNaN(condition.conditionValue) ? condition.conditionValue : condition.conditionValue;
				if (collapsibleFilterValue) {
					filteredData = DataUtilities.filterDataSet(condition.conditionType, condition.conditionOperator, condition.conditionField, (conditionValue === 'datadate' ? dataDate : (condition.useInternalConditionValue ? collapsibleFilterValue : conditionValue)), dataSource.data, null);
				} else {
					filteredData = DataUtilities.filterDataSet(condition.conditionType, condition.conditionOperator, condition.conditionField, conditionValue === 'datadate' ? dataDate : conditionValue, dataSource.data, null);
				}
				if (Array.isArray(filteredData)) {
					data = filteredData;
				} else {
					data = [filteredData];
				}
			} else {
                data = dataSource.data;
            }

            setPager(showPagination);

            if (pivotEnabled) {
                // Pivot row

                // Setup first column
                const newColumns = [];
                const category = fields[0].name;
                newColumns.push(
                    {
                        id: pivotHeading === true ? category : "field"
                        , alignment: 'left'
                        , disablePadding: false
                        , formatters: [
                            {
                                type: "style",
                                config: {
                                    fontWeight: "bold"
                                }
                            }
                          ]
                        , label: ""
                        , condition: null
                    }
                );

                // Lookup the formatters and set first field as Category
                const formatters = getFieldFormatters(dataSource, category);

                // Sort data logically by Category
                data = data.sort(compareValues(category));
                let idName;

                // Create pivoted columns
                for (let d = 0; d < data.length; d++) {
                    // Column ID is the Category field from each Data Row + '--' + d .... e.g periodenddate-01202021--d
                    idName = data[d][category] ? ((data[d][category]).toString() + '--' + d) : 'null-' + category + '--' + d;

                    // Build Column object
                    newColumns.push(
                        {
                            id: pivotHeading === true ? category + '-' + idName : "value"
                            , alignment: 'left'
                            , disablePadding: false
                            , formatters: formatters.length > 0 ? formatters : []
                            , label: formatValue(data[d][category], formatters) // d[fields[0].name]
                            , condition: null
                        }
                    );
                }
                setColumns(newColumns);

                // Build data rows
                const newData = [];
                const row = data.length > 0 ? data[0] : null;
                if (row !== null) {
                    if (newColumns.length <= 2) {
                        for (let f of fields) {

                            // Lookup aliases and formatters
                            const label = getFieldAlias(dataSource, f.name);
                            const alias = label !== null ? label : f.name;
                            const formatters = getFieldFormatters(dataSource, f.name);

                            // let aliasIndex = -1;
                            // for (let s = 0; s < dataSource.fields.length; s++) {
                            //     const fieldName = dataSource.fields[s];
                            //     if (fieldName.indexOf(f.name) > 0) {
                            //         aliasIndex = s;
                            //         break;
                            //     }
                            // }
                            // const alias = aliasIndex !== -1 ? dataSource.aliases[aliasIndex] : f.name;

                            const newRow = {
                                field: alias,
                                value: row[f.name],
                                alignment: f.alignment,
                                disablePadding: f.disablePadding,
                                minWidth: f.minWidth,
                                formatters: f.formatters.length > 0 ? f.formatters : formatters,
                                // formatters: f.formatters
                                condition: f?.condition !== undefined ? f.condition : null
                            };

                            newData.push(newRow);
                        }
                    } else {
                        // Row Object
                        // --> skip first row, start at position 1 not 0 in array, first row is the heading built from newColumns above
                        for (let f = 1; f < fields.length; f++) {
                            let newD = {};

                            // Get Alias for field --> first column will be defined by the Category
                            const label = getFieldAlias(dataSource, fields[f].name);
                            const alias = label !== null ? label : fields[f].name;
                            newD[category] = alias;

                            // Loop through data to build out all columns
                            for (let d = 0; d < data.length; d++) {

                                // Get the cells formatters
                                const formatters = getFieldFormatters(dataSource, fields[f].name);
                                newD.formatters = fields[f].formatters.length > 0 ? fields[f].formatters : formatters;

                                // Match the data value to the column name
                                const columnName = (data[d][category]).toString() + '--' + d;
                                newD[category + '-' + columnName] = data[d][fields[f].name];

                            }
                            //console.log(newD);
                            newData.push(newD);
                        }
                    }
                }
                // console.log(newData);
                setRows(newData);
            } else {
                // Normal table
                const newColumns = [];
                for (let f of fields) {

                    // Lookup the alias and formatters
                    const label = getFieldAlias(dataSource, f.name);
                    const alias = label !== null ? label : f.name;
                    const formatters = getFieldFormatters(dataSource, f.name);

                    newColumns.push(
                        {
                            id: f.name
                            ,alignment: f.alignment
                            ,disablePadding: f.disablePadding
                            ,minWidth: f.minWidth
                            ,formatters: f.formatters.length > 0 ? f.formatters : formatters
                            // ,formatters: f.formatters
                            ,label: alias
                            ,condition: f?.condition !== undefined ? f.condition : null
                        }
                    )
                }
                setColumns(newColumns);

                const newData = [];
                for (let d of data) {
                    const newD = {};
                    for (let f = 0; f < fields.length; f++) {
                        newD[fields[f].name] = d[fields[f].name];
                    }
                    newD.id = d[idField];
                    newData.push(newD);
                }

                setRows(newData);
            }
        }

    }, [dataSource.data, t]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Table Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'Table'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

    // useEffect(() => {
    //     if (defaultRowsPerPage) {
    //         alert(defaultRowsPerPage);
    //         setRowsPerPage(defaultRowsPerPage);
    //     }
    // }, [defaultRowsPerPage]);

    function isFunction(functionToCheck) {
        return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = rows.map((n) => n.name);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, name) => {
        // NOT IMPLEMENTED
        return;

        if (pivotEnabled){
            return;
        }

        const selectedIndex = selected.indexOf(name);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // const handleChangeDense = (event) => {
    //     setDense(event.target.checked);
    // };

    const isSelected = (name) => selected.indexOf(name) !== -1;

    //const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

	const deleteRow = (row, deleteConfig) => {

		// Before we post get the user's ObjectID
		setTimeout(function () {

			const postBody = {
				[deleteConfig.postBodyField]: row[deleteConfig.idField]
			};

			const apiUrl = '{0}{1}?db={2}'.format(config.data.authentication.api, deleteConfig.endpointPath, config.data.authentication.apiSource);

			// Here we pass a date time stamp to ensure the latest config is always loaded
			axios
				.delete(apiUrl, {
					data: postBody
				})
				.then(function (response) {

					dispatch(appSetAlert(null, '{0} was successfully deleted.'.format(row[deleteConfig.friendlyName]), 'success', 10000));

					// Refresh Dependant Data Sources
					dispatch(dataReloadDependants( deleteConfig.reloadDataSources ));

				})
				.catch(function (error) {
					console.log('**ERROR**', error);

					dispatch(appSetAlert('Whoops!', 'Something went seriously wrong. Please try again.', 'error', 10000));

				});

		}, JSON.parse(axiosDelay()));

	};

	const filterRow = (row, filterConfig) => {
		dispatch(dataAddFilter(dataSourceName + '-' + filterConfig.idField, filterConfig.idField, " eq '{0}'".format(row[filterConfig.idField]), filterUpdateConditionAction.REPLACE, true, false));
	};

    // TODO: Build once when data changes - refer to Portfolio
    function BuildRow(props) {
        const { index, row } = props;
        const isItemSelected = isSelected(row.id);
        // const labelId = `enhanced-table-checkbox-${index}`;
        const [collapsibleOpen, setCollapsibleOpen] = React.useState(false);

        const tableCells = [];
        let collapsibleTable = [];
        if (collapsible) {
            const collapsibleDS = dataSources.find(s => s.name.toLowerCase().trim() === compConfig.element.collapsible.configuration.dataSource.name.toLowerCase().trim());

            if (collapsibleDS.data) {
                const collapsibleData = collapsibleDS.data;
                collapsibleTable = collapsibleData.filter(d => d[compConfig.element.collapsible.configuration.dataSource.fieldMatch] === row[compConfig.element.collapsible.configuration.dataSource.fieldMatch]);

                if (collapsibleTable.length > 0) {
                    tableCells.push(<TableCell key={columns.length * 2} align={'left'} padding="normal">
                                        <IconButton aria-label="expand row" size="small" onClick={() => setCollapsibleOpen(!collapsibleOpen)}>
                                            {collapsibleOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        </IconButton>
                                    </TableCell>);
                } else {
                    tableCells.push(<TableCell key={columns.length * 2} align={'left'} padding="normal"></TableCell>);
                }
            }
        }
        for (let c = 0; c < columns.length; c++) {
            const column = columns[c];

            let formatters = column.formatters;
            if (row.formatters && pivotEnabled && c > 0) {
                formatters = row.formatters;
            }

            let alignment = column.alignment;
            if (row.alignment && pivotEnabled && c > 0) {
                alignment = row.alignment;
            }

            let disablePadding = column.disablePadding;
            if (row.disablePadding && pivotEnabled && c > 0) {
                disablePadding = row.disablePadding;
            }

            let bkgColor = null;
            if (column.condition !== null) {
                const performanceEquality = getRangeVal(row[column.id], column.condition);
                if (column.condition.type.toLowerCase() === 'range') {
                    bkgColor = (performanceEquality === 'equal' ? ragStyle.amber : (performanceEquality === 'greater' ? ragStyle.red : (performanceEquality === 'less' ? ragStyle.green : ragStyle.amber)));
                } else if (column.condition.type.toLowerCase() === 'value') {
                    bkgColor = (performanceEquality === 'equal' ? ragStyle.amber : (performanceEquality === 'greater' ? ragStyle.red : (performanceEquality === 'less' ? ragStyle.green : ragStyle.amber)));
                } else if (column.condition.type.toLowerCase() === 'ragmatch') {
                    bkgColor = getRAGStyleValue(performanceEquality, ragStyle);
                }
            }

            let formattedValue = row[column.id];
			let isIconic = false;
            for (const formatter of formatters) {
                if (formatter.type.toUpperCase() === 'AVATAR') {
                    formattedValue = formatAvatar(formattedValue, formatter.config);
					isIconic = true;
					break;
                } else if (formatter.type.toUpperCase() === 'DATE') {
                    formattedValue = formatDate(formattedValue, formatter.config);
                    // formattedValue = DateUtilities.dateString(formattedValue);
                } else if (formatter.type.toUpperCase() === 'NUMBER') {
                    formattedValue = formatNumber(formattedValue, formatter.config);
                } else if (formatter.type.toUpperCase() === 'STYLE') {
                    formattedValue = formatStyle(formattedValue, formatter.config, row[column.id], formatter.condition);
                } else if (formatter.type.toUpperCase() === 'TEXT') {
                    formattedValue = formatText(formattedValue, formatter.config);
                } else if (formatter.type.toUpperCase() === 'LINK') {
                    formattedValue = FormatLink(formattedValue, formatter.config);
					isIconic = true;
					break;
                } else if (formatter.type.toUpperCase() === 'MODAL') {
                    formattedValue = FormatModalLink(formattedValue, formatter.config);
				} else if (formatter.type.toUpperCase() === 'DELETE') {
					formattedValue = <ConfirmationButton type="delete" tooltip={formatter.config.tooltip} handleConfirm={() => { deleteRow(row, formatter.config) }}/>
					isIconic = true;
					break;
				} else if (formatter.type.toUpperCase() === 'FILTER') {
					formattedValue = <ConfirmationButton type="filter" tooltip={formatter.config.tooltip} handleConfirm={() => { filterRow(row, formatter.config) }}/>
					isIconic = true;
					break;
				} else if (formatter.type.toUpperCase() === 'FILTERLINK') {
					formattedValue = <div onClick={() => { filterRow(row, formatter.config) }}><LinkIcon link={formatter.config.link}></LinkIcon></div>
					isIconic = true;
					break;
                } else if (formatter.type.toUpperCase() === 'SUFFIX') {
                    formattedValue = formatSuffix(formattedValue, formatter.config);
                } else if (formatter.type.toUpperCase() === 'GOODBAD') {
                    formattedValue = formatGoodBad(formattedValue, formatter.config, row[column.id], formatter.condition);
                } else if (formatter.type.toUpperCase() === 'DURATION') {
                    if (dataSource.data.length > 0) {
                        formattedValue = formatDuration(dataSource.data[0].min, dataSource.data[0].max, formatter.config);
                    }
                } else {

                }
            }

            tableCells.push(<TableCell key={c} align={alignment === 'left' ? 'left' : 'right'} padding={disablePadding ? 'none' : 'normal'} style={{backgroundColor: bkgColor}}>
								{isIconic &&
									<div>{formattedValue}</div>
								}
								{!isIconic &&
									<Typography variant={variant}>{formattedValue}</Typography>
								}
                            </TableCell>);
        }
        // console.log('building rows');
        return (
            <React.Fragment>
                <TableRow
                    hover
                    onClick={(event) => handleClick(event, row.id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={index}
                    selected={isItemSelected}
                >
                    {!pivotEnabled &&
                        <React.Fragment>
                            {/*<TableCell padding="checkbox">*/}
                            {/*    <Checkbox*/}
                            {/*     checked={isItemSelected}*/}
                            {/*     inputProps={{'aria-labelledby': labelId}}*/}
                            {/*    />*/}
                            {/*</TableCell>*/}
                        </React.Fragment>
                    }

                    {tableCells}
                </TableRow>
                {(collapsibleTable.length > 0) &&
					<TableRow>
						<TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length + 1}>
							<Collapse in={collapsibleOpen} timeout="auto" unmountOnExit>
								<Construct layout={{configuration: compConfig.element.collapsible}} collapsibleFilterValue={collapsibleTable[0][compConfig.element.collapsible.configuration.dataSource.fieldMatch]}/>
							</Collapse>
						</TableCell>
					</TableRow>
                }
            </React.Fragment>
        );
    };

    function getRangeVal(initVal, condition) {

        if (condition.type.toLowerCase() === 'range') {
            if (initVal >= parseFloat(condition.value.min) && initVal <= parseFloat(condition.value.max)) {
                return "equal";
            } else if (initVal >= parseFloat(condition.value.min) && initVal >= parseFloat(condition.value.max)) {
                return "greater";
            } else if (initVal <= parseFloat(condition.value.min) && initVal <= parseFloat(condition.value.max)) {
                return "less";
            } else {
                return '';
            }
        } else if (condition.type.toLowerCase() === 'value') {
            if (initVal === parseFloat(condition.value)) {
                return "equal";
            } else if (initVal > parseFloat(condition.value)) {
                return "greater";
            } else if (initVal < parseFloat(condition.value)) {
                return "less";
            } else {
                return '';
            }
        } else if (condition.type.toLowerCase() === 'ragmatch') {
            return getRAGStyleProperty(initVal, condition);
        } else {
            return '';
        }
    };

    return (
		<Root className={classes.root}>
            {showProgress && dataSource.fetching &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
                <div className={classes.linearProgress}></div>
            }

            <Paper className={classes.paper}>

                {(title || subtitle) &&
                    <SmartTableToolbar id={id} title={title} titleVariant={titleVariant} subTitle={subtitle} subTitleVariant={subTitleVariant} numSelected={selected.length} addNew={addData} />
                }
                <TableContainer className={showPager ? classes.tableContainer : classes.tableContainerSansPager}>
                    {columns.length > 0 &&
                    <Table
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        size={dense ? 'small' : 'medium'}
                        aria-label="enhanced table"
                        style={{minWidth: minimumWidth}}
                    >
                        {/*{pivot === false &&*/}
                        {((pivotEnabled && pivotHeading) || (!pivotEnabled)) &&
                        <SmartTableHead
                            classes={classes}
                            columns={columns}
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                            collapsible={collapsible}
                        />
                        }
                        <TableBody>
                            {stableSort(rows, getComparator(order, orderBy))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => {
                                    return <BuildRow key={index} index={index} row={row} />
                                })}
                            {/*{emptyRows > 0 && (*/}
                            {/*    <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>*/}
                            {/*        <TableCell colSpan={6} />*/}
                            {/*    </TableRow>*/}
                            {/*)}*/}
                        </TableBody>
                    </Table>
                    }
                </TableContainer>
                {showPager && columns.length > 0 &&
                    <TablePagination
                        rowsPerPageOptions={ rowsPerPageOptions }
                        component="div"
                        count={rows.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                }
            </Paper>
        </Root>
    );
}
